import { Fragment, type ReactNode } from 'react';

import { buildActivityAssigneeOptions } from '@apps/web/components/home/activity_assignment';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuLabel,
    ContextMenuSeparator,
    ContextMenuSub,
    ContextMenuSubContent,
    ContextMenuSubTrigger,
    ContextMenuTrigger,
} from '@apps/web/components/ui/context_menu';
import { SelectionCheckboxIndicator } from '@apps/web/components/ui/selection_checkbox';
import {
    getConversationAvatarToneClass,
    UserAvatar,
} from '@apps/web/components/ui/user_avatar';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { cn } from '@apps/web/lib/utils';
import { useActivityEventAssignees } from '@apps/web/swr_data_fetching/activity_event_assignments';
import { type OrganizationConversationMetadata } from '@libraries/typescript/api_client';

function inboxAvatarName(
    conversation: OrganizationConversationMetadata,
): string {
    return (
        conversation.caregiver_name ||
        conversation.external_contact_name ||
        conversation.phone_number
    );
}

type ConversationInboxRowAvatarProps = {
    readonly conversation: OrganizationConversationMetadata;
    readonly picked: boolean;
    readonly selecting: boolean;
    readonly onToggle: () => void;
};

// Quo: idle is a 40px initials circle. Hovering the circle itself, or any
// selection, replaces it with an 18px square check in the same slot — no
// leftover disc or wash behind the box. Hovering the rest of the row leaves
// the initials alone: a checkbox that appears while the cursor crosses the
// list reads as a prompt to select, when the row is there to be opened.
export function ConversationInboxRowAvatar({
    conversation,
    picked,
    selecting,
    onToggle,
}: ConversationInboxRowAvatarProps) {
    const { formatName } = useFormatPersonName();
    const showCheck = selecting || picked;
    return (
        <button
            aria-label={
                picked ? 'Deselect conversation' : 'Select conversation'
            }
            aria-pressed={picked}
            className="group/avatar relative flex size-10 shrink-0 items-center justify-center"
            onClick={(event) => {
                event.stopPropagation();
                onToggle();
            }}
            type="button"
        >
            {showCheck ? null : (
                <UserAvatar
                    className="size-10 group-hover/avatar:hidden"
                    colorKey={conversation.id}
                    displayName={formatName(inboxAvatarName(conversation))}
                    toneClassName={getConversationAvatarToneClass(
                        conversation.id,
                    )}
                />
            )}
            <span
                className={cn(
                    'pointer-events-none absolute inset-0 items-center justify-center',
                    showCheck ? 'flex' : 'hidden group-hover/avatar:flex',
                )}
            >
                <SelectionCheckboxIndicator
                    checked={picked}
                    data-testid="conversations-inbox-select-check"
                />
            </span>
        </button>
    );
}

// The member list is fetched here so it is requested when the submenu
// renders, not for every row that merely has a menu.
function AssignSubmenu({
    onAssign,
    organizationId,
}: {
    readonly onAssign: (userId: string | null) => void;
    readonly organizationId: string | null;
}) {
    const { data: assigneesData } = useActivityEventAssignees(
        organizationId,
        organizationId ? [organizationId] : [],
    );
    const assigneeOptions = buildActivityAssigneeOptions(
        assigneesData?.users ?? [],
    );

    return (
        <ContextMenuSub>
            <ContextMenuSubTrigger>Assign to…</ContextMenuSubTrigger>
            <ContextMenuSubContent>
                {assigneeOptions.map((option) => (
                    <ContextMenuItem
                        key={option.id}
                        onSelect={() => onAssign(option.id)}
                    >
                        {option.label}
                    </ContextMenuItem>
                ))}
                <ContextMenuSeparator />
                <ContextMenuItem onSelect={() => onAssign(null)}>
                    Unassign
                </ContextMenuItem>
            </ContextMenuSubContent>
        </ContextMenuSub>
    );
}

// Right-click on a row. Which menu appears follows the selection, the way a
// file manager does it: right-clicking inside a selection acts on the whole
// selection, right-clicking elsewhere acts on that one row and leaves the
// selection alone. Acting on twelve threads because the cursor was one row
// off is the failure this ordering avoids.
export function ConversationInboxRowContextMenu({
    children,
    isUnread,
    selectedCount,
    organizationId,
    canAssign,
    onMarkReadState,
    onAssign,
}: {
    readonly children: ReactNode;
    readonly isUnread: boolean;
    // Zero when the row is not part of the current selection.
    readonly selectedCount: number;
    readonly organizationId: string | null;
    // Assignment is behind conversation_ownership_enabled. The bottom bar
    // hides its picker when the flag is off; this menu must agree, or it
    // offers an action the rest of the inbox will not show the result of.
    readonly canAssign: boolean;
    readonly onMarkReadState: (unreadCount: 0 | 1) => void;
    readonly onAssign: (userId: string | null) => void;
}) {
    const isBulk = selectedCount > 0;

    return (
        <ContextMenu>
            {/* No asChild: the row component takes only its own props and
                forwards no ref, so Radix would have nothing to attach the
                right-click handler to and the menu would never open. Radix
                renders its own element here instead. */}
            <ContextMenuTrigger>{children}</ContextMenuTrigger>
            <ContextMenuContent
                className="w-52"
                data-testid="conversations-inbox-row-context-menu"
            >
                {isBulk ? (
                    <Fragment>
                        <ContextMenuLabel>
                            {selectedCount} selected
                        </ContextMenuLabel>
                        <ContextMenuSeparator />
                    </Fragment>
                ) : null}
                {/* One action list for both scopes. A selection offers both
                    read states because its rows can disagree; a single row
                    offers only the one that changes anything. */}
                {isBulk || isUnread ? (
                    <ContextMenuItem onSelect={() => onMarkReadState(0)}>
                        Mark as read
                    </ContextMenuItem>
                ) : null}
                {isBulk || !isUnread ? (
                    <ContextMenuItem onSelect={() => onMarkReadState(1)}>
                        Mark as unread
                    </ContextMenuItem>
                ) : null}
                {canAssign ? (
                    <AssignSubmenu
                        onAssign={onAssign}
                        organizationId={organizationId}
                    />
                ) : null}
            </ContextMenuContent>
        </ContextMenu>
    );
}
